// react
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// icons
import { SlMagnifier } from "react-icons/sl";

// data
import { searchData } from "@/data/searchData";

// i18n
import { useTranslation } from "react-i18next";

const SearchInput = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<typeof searchData>([]);
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.trim() === "") {
      setResults([]);
      return;
    }

    const searchResults = searchData.filter((item) => {
      const searchValue = value.toLowerCase();
      return (
        item.title.toLowerCase().includes(searchValue) ||
        item.keywords.some((keyword) => keyword.toLowerCase().includes(searchValue))
      );
    });

    setResults(searchResults);
    setIsOpen(true);
  };

  const handleResultClick = (path: string) => {
    navigate(path);
    setSearchTerm("");
    setResults([]);
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className="flex h-[40px] w-[240px] items-center gap-2 rounded-md bg-[#f0f0f3]/50 py-2"
        style={{ paddingInlineStart: "0.75rem", paddingInlineEnd: "0.375rem" }}
      >
        <SlMagnifier className="h-4 w-4 text-[#afafaf]" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder={t("common.search")}
          dir="auto"
          className="w-full border-none bg-transparent text-base font-medium leading-normal outline-none placeholder:text-[#afafaf] focus:border-none focus:ring-0"
        />
      </div>

      {isOpen && results.length > 0 && (
        <div className="absolute z-50 mt-2 w-full rounded-md bg-[#e8f0ec] py-2 shadow-lg" style={{ insetInlineStart: 0 }}>
          {results.map((result, index) => (
            <div
              key={index}
              onClick={() => handleResultClick(result.path)}
              className="cursor-pointer px-4 py-2 text-sm font-bold text-primary hover:bg-[#CCCDD54D]"
            >
              {result.title}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchInput;
